// Per-browser settings, same pattern as sites.novara.com and it-assets. Saved in localStorage
// only; nothing reaches the server. Storage can throw (private windows, blocked site data), so
// every access is guarded and the page carries on with defaults.

export type Theme = "system" | "light" | "dark";
export type Landing = "ask" | "admin";

export interface Prefs {
  theme: Theme;
  landing: Landing;
}

export const PREFS_KEY = "novara-askhr-prefs";
// Before Settings existed, only the theme was saved, under this key. Read once, then dropped.
const LEGACY_THEME_KEY = "novara-askhr-theme";

export const DEFAULT_PREFS: Prefs = { theme: "system", landing: "ask" };

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function safeStorage(): Store | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

const isTheme = (v: unknown): v is Theme => v === "system" || v === "light" || v === "dark";

export function loadPrefs(storage: Store | undefined = safeStorage()): Prefs {
  try {
    const raw = storage?.getItem(PREFS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Prefs>;
      return {
        theme: isTheme(p.theme) ? p.theme : DEFAULT_PREFS.theme,
        landing: p.landing === "admin" ? "admin" : "ask",
      };
    }
    const legacy = storage?.getItem(LEGACY_THEME_KEY);
    if (isTheme(legacy)) return { ...DEFAULT_PREFS, theme: legacy };
  } catch {}
  return { ...DEFAULT_PREFS };
}

export function savePrefs(p: Prefs, storage: Store | undefined = safeStorage()): void {
  try {
    storage?.setItem(PREFS_KEY, JSON.stringify(p));
    storage?.removeItem(LEGACY_THEME_KEY);
  } catch {}
}

export function resetPrefs(storage: Store | undefined = safeStorage()): void {
  try {
    storage?.removeItem(PREFS_KEY);
    storage?.removeItem(LEGACY_THEME_KEY);
  } catch {}
}

export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}
